import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import {
  IComment,
  ITag,
  ITask,
  ITaskCreate,
  ITaskLog,
  ITaskStatus,
  ITaskType,
  ITaskUpdate,
  ITaskViewResponse,
  IUser,
} from '@/models';

import { http } from './http';
import { IGetParams, IPaginatedListResponse } from './types';

export const TaskApi = {
  getTasks: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<ITask>>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/`, { params });
  },
  getTask: async (id: string): Promise<AxiosResponse<ITaskViewResponse>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/${id}`);
  },
  getTaskLogs: async (
    taskId: string,
  ): Promise<AxiosResponse<{ logs: ITaskLog[]; users: IUser[] }>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/${taskId}/logs`);
  },
  createTask: async (data: ITaskCreate): Promise<AxiosResponse<ITask>> => {
    return http.post(`api/${ApiVersion.V1}/tasks/`, data);
  },
  updateTask: async (
    data: Partial<ITaskCreate | ITaskUpdate>,
  ): Promise<AxiosResponse<ITask>> => {
    return http.put(`api/${ApiVersion.V1}/tasks/${data._id}`, data);
  },
  deleteTask: async (id: string): Promise<AxiosResponse<ITask>> => {
    return http.delete(`api/${ApiVersion.V1}/tasks/${id}`);
  },
  // task comments
  getTaskComments: async (
    taskId: string,
  ): Promise<AxiosResponse<IComment[]>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/${taskId}/comments`);
  },
  createTaskComment: async (
    taskId: string,
    description: string,
    mentions?: IComment['mentions'],
  ): Promise<AxiosResponse<IComment>> => {
    return http.post(`api/${ApiVersion.V1}/tasks/${taskId}/comments`, {
      description,
      mentions,
    });
  },
  getTaskStatuses: async (): Promise<AxiosResponse<ITaskStatus[]>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/statuses`);
  },
  getTaskTags: async (): Promise<AxiosResponse<ITag[]>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/tags`);
  },
  createTaskTag: async (tag: string): Promise<AxiosResponse<ITag>> => {
    return http.post(`api/${ApiVersion.V1}/tasks/tags`, { tag });
  },
  getTaskTypes: async (): Promise<AxiosResponse<ITaskType[]>> => {
    return http.get(`api/${ApiVersion.V1}/tasks/types`);
  },
  createTaskType: async (type: string): Promise<AxiosResponse<ITaskType>> => {
    return http.post(`api/${ApiVersion.V1}/tasks/types`, { type });
  },

  removeTaskComment: async ({
    taskId,
    _id,
  }: {
    taskId: string;
    _id: string;
  }): Promise<AxiosResponse<ITask>> => {
    return http.delete(`api/${ApiVersion.V1}/tasks/${taskId}/comments/${_id}`);
  },
  updateTaskComment: async (data: {
    task: string;
    _id: string;
    description: string;
    mentions?: IComment['mentions'];
  }): Promise<AxiosResponse<ITask>> => {
    return http.put(
      `api/${ApiVersion.V1}/tasks/${data.task}/comments/${data._id}`,
      { description: data.description, mentions: data.mentions },
    );
  },
};
